/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { DatePipe } from '@angular/common'
import { AfterViewInit, Component, OnInit, ViewChild, DestroyRef, inject } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { UnespCoreAuthService, UnespCoreMessageService } from 'src/libs/unesp-core'
import { ComissaoJulgadoraService } from '../comissao-julgadora-service'
import { TipoConcurso, TipoConcursoDTA, TipoConcursoLabel } from 'src/app/enums/tipo-concurso'
import { Concurso } from 'src/app/models/concurso'
import { DefaultPaginatorComponent } from 'src/app/modules/shared'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'inscricoes-lista-concurso-examinador-unicamp',
  templateUrl: './lista-concurso-examinador.component.html',
  styleUrls: ['./lista-concurso-examinador.component.scss'],
})
export class ListaConcursoExaminadorComponent implements AfterViewInit, OnInit {
  private destroyRef = inject(DestroyRef)

  @ViewChild('paginator', { static: true }) paginator!: DefaultPaginatorComponent
  @ViewChild(MatSort) sort!: MatSort

  totalElements: number = 0
  sortField = 'inicio'
  sortDirection = 'desc'
  colunasGrid: string[] = ['id', 'campus', 'processo', 'titulo', 'inscricao', 'tipo', 'andamento', 'action']
  dataSource: MatTableDataSource<Concurso> = new MatTableDataSource()
  datepipe: DatePipe = new DatePipe('pt-BR')

  tipoConcurso = TipoConcurso
  tipoConcursoDTA = TipoConcursoDTA

  constructor(
    private comissaoJulgadorService: ComissaoJulgadoraService,
    private unespCoreMessageService: UnespCoreMessageService,
    private unespCoreAuthService: UnespCoreAuthService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit() {
    this.sort?.sortChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.sortField = this.sort.active
      this.sortDirection = this.sort.direction || 'asc'
      this.paginator.pageIndex = 0
      this.getData()
    })

    this.paginator?.page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.getData())
    setTimeout(() => this.getData(), 0)
  }

  public getData(): void {
    this.comissaoJulgadorService
      .listarConcursos('', this.paginator.pageIndex, this.paginator.pageSize, '', this.sortField, this.sortDirection)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          const concursos = data['content'];
          this.dataSource = new MatTableDataSource(concursos);
          this.totalElements = data['totalElements'];
            
          const temPresidente = concursos.some((item: any) => item.papelExaminador === 'PRESIDENTE');
  
          if (temPresidente) {
            if (!this.colunasGrid.includes('classificacao')) {
              this.colunasGrid.push('classificacao');
            }
          } else {
            this.colunasGrid = this.colunasGrid.filter(col => col !== 'classificacao');
          };
        },
        error: (err) => {
          console.error('Erro ao listar concursos:', err);
          this.unespCoreMessageService.showMessageError(
            err?.error?.message ?? 'Erro ao carregar a lista de concursos.'
          );
        }
      });
  }

  getTipo(tipo: string): string {
    return TipoConcursoLabel.get(tipo) ?? ''
  }

  canOpenOptions(): boolean {
    return this.unespCoreAuthService.hasPermissionBasedRoles(['EXAMINADOR'])
  }

  mostrarProvasBtn(concurso: Concurso): boolean {
    return ['EXAMINADOR'].includes(concurso.tipo)
  }
}
